import { ASSIST } from './assist';
import { BikeConfig } from './config';
import { InputFrame } from './input';
import { Quat, Vec3, vec, dot, clamp, length, quatIdentity, rotate } from './math';
import { valueNoise } from './noise';
import { createEngine, DriveOutput, EngineState, stepDrivetrain } from './physics/drivetrain';
import { judgeLanding, Landing } from './physics/landing';
import { checkLimits, FallCause, newLimitInput } from './physics/limits';
import { SURFACE_FEEL, WANDER_SCALE } from './physics/surface';
import { SpinInput, SpinResult, stepWheelSpin } from './physics/wheel';
import { TyreParams } from './physics/tyre';
import { createRider, legDamping, newPostureCues, RiderState, stepRider } from './rider';
import { ColliderKind, KIND_FEEL, KINDS, ObstacleField } from './obstacles';
import { collide, newCollideOut } from './physics/collide';
import { Surface, SURFACE_GRIP, Terrain, TerrainSample } from './terrain';

export const GRAVITY = 9.81;

/**
 * Body-frame points that touch the ground when the bike is down: hubs, footpegs/side panels, bar ends, and the top
 * (seat, rear fender tip, front number plate), so a bike on its back lies on them instead of sinking its top into the
 * ground (owner screenshot 2026-10-05). The hub points sit above full suspension travel, so they never fight the
 * wheel rays on an upright bike.
 */
const CHASSIS_POINTS: readonly Vec3[] = [
  vec(0, -0.3, 0.72), vec(0, -0.3, -0.72),
  vec(0.35, 0, 0.5), vec(-0.35, 0, 0.5), vec(0.35, 0, -0.5), vec(-0.35, 0, -0.5),
  vec(0.4, 0.35, 0.6), vec(-0.4, 0.35, 0.6),
  vec(0, 0.5, -0.3), vec(0, 0.45, -0.8), vec(0, 0.45, 0.65),
];
/** Body-frame points tested against tree trunks: hubs, frame centre line, footpegs, bar ends. At most 0.5 m apart. */
const OBSTACLE_POINTS: readonly Vec3[] = [
  vec(0, -0.3, 0.72), vec(0, -0.3, -0.72), vec(0, 0, 0), vec(0, 0.2, 0.4), vec(0, 0.2, -0.4),
  vec(0.3, 0.1, 0.2), vec(-0.3, 0.1, 0.2), vec(0.4, 0.35, 0.6), vec(-0.4, 0.35, 0.6),
];
const OBSTACLE_REACH = 1.5; // m around the bike centre that can touch a trunk
const OBSTACLE_QUERY = 1.8; // m round the bike centre its capsules and wheels can reach
const ROT = new Float64Array(9);
const HIT = newCollideOut();

/** Row-major rotation matrix of a unit quaternion (world = m * local). */
function quatToMatrix(q: Quat, m: Float64Array): void {
  const { x, y, z, w } = q;
  m[0] = 1 - 2 * (y * y + z * z); m[1] = 2 * (x * y - z * w); m[2] = 2 * (x * z + y * w);
  m[3] = 2 * (x * y + z * w); m[4] = 1 - 2 * (x * x + z * z); m[5] = 2 * (y * z - x * w);
  m[6] = 2 * (x * z - y * w); m[7] = 2 * (y * z + x * w); m[8] = 1 - 2 * (x * x + y * y);
}
const MAX_SPEED = 200;  // m/s, beyond this the state has blown up
const MAX_SPIN = 100;   // rad/s
const WANDER_SEED = 4242;
const RUT_PROBE = 0.1;  // m either side of the tyre where the rut walls are felt
const LANDING_MIN_AIR = 0.2; // s of flight before a touchdown is judged as a landing
const LANDING_WINDOW = 0.5; // s after a touchdown in which the hit is still playing out (safepoints wait for it)
const LIMITS = newLimitInput();
const CUES = newPostureCues();
/** Ground probe ahead for the posture controller: metres ahead of the bike centre. */
const PROBE_AHEAD = [0, 1.5, 3, 4.5, 6] as const;
const SPIN_OUT = 1.45; // rad (83 deg)
/** Front tyre grip use where the rider starts easing the lean .. has eased it to FRONT_PUSH_MIN of his limit. */
const FRONT_PUSH: readonly [number, number] = [0.8, 1.2], FRONT_PUSH_MIN = 0.35;
/** Throttle up to which a rider is holding a steady part throttle through a turn (not stabbing it). */
const STEADY_THROTTLE = 0.6, STEADY_TIME = 0.8; // s
/** rad past the rider's lean limit where a slow bike going over gets a dab of the inside foot. */
const DAB_OVER_LEAN = 0.05;
/** Lean (rad) where the rider starts .. fully rolls back a spinning rear; rear slip (x peak) where that starts .. is full. */
const LEAN_SPIN: readonly [number, number] = [0.1, 0.35], REAR_SPIN: readonly [number, number] = [1.2, 2.2], REAR_SPIN_CUT = 0.9;
/** Real mode, above footMaxSpeed: past the lean limit + margin the rider stands the bike back up (N*m/rad, N*m*s/rad). */
const LEAN_GUARD_KP = 6000, LEAN_GUARD_KD = 800, LEAN_GUARD_MARGIN = 0.05;
/** Paddling backwards: m/s under which it can start, s the lever is held first, m/s it reaches, N it pushes at most, N per m/s. */
const PADDLE_START = 0.3, PADDLE_HOLD = 0.4, PADDLE_SPEED = 1.2, PADDLE_FORCE = 350, PADDLE_GAIN = 600;
/** Share of the slide angle (heading vs travel) the bars swing into the slide (opposite lock). */
const SLIDE_LOCK = 0.6, SLIDE_LOCK_FROM = 0.15;
/** Share of the front lever the rider gives back at the top of a stoppie (the rest is what can still flip it). */
const STOPPIE_EASE = 0.6;
/** Direction of the whip's yaw for a positive steer (the same way the bike turns on the ground). */
const WHIP_SIGN = 1;
/** Steer input units per second a rider turns the bars at (full lock in about half a second, at speed). */
const STEER_RATE = 2.2;
/** m/s under which the rider's feet are down and hold a stopping bike upright. */
const STOP_FEET = 2;
/** N*m both feet on the ground can push a stopping bike back up with. */
const STOP_TORQUE = 1400;
/** rad of pitch in the air past which the rider helps the bike back toward level, and the N*m/rad he does it with. */
const AIR_PITCH_HELP = 0.6, AIR_PITCH_KP = 250;
const SPIN_TIME = 0.5; // s   // rad of sideslip (heading vs travel) at speed that ends in a fall

export interface WheelState {
  compression: number;
  contact: boolean;
  omega: number;     // rad/s, + rolling forward
  load: number;      // N from the suspension
  slipRatio: number; // (omega*R - v) / v, + = spinning, -1 = locked
  usage: number;     // tyre friction-circle use, > 1 = sliding
}

export type { FallCause } from './physics/limits';

/** A hard contact with an obstacle this step. */
export interface Hit { kind: ColliderKind; speed: number; part: 'bike' | 'rider' | 'bars' }

export interface BikeTelemetry {
  speed: number;      // m/s along the bike's forward axis
  rearSlip: number;   // rear drive slip / peak slip; > 1 means wheelspin (audio, coach, particles, drills)
  frontLock: boolean; // front wheel locked or sliding under the brake while moving
  airborne: boolean;
  fallen: boolean;
  // Phase 2 (coach physics).
  rpm: number;
  gear: number;
  frontBrake: number;  // N*m applied this step
  rearBrake: number;
  rearLock: boolean;
  frontWashout: boolean;
  wheelie: boolean;    // rear on the ground, front up
  pitch: number;       // rad, nose up + relative to the horizon
  roll: number;        // rad, + leaned left
  sideslip: number;    // rad between heading and travel on the ground
  landing: Landing | null; // set on the step of a judged touchdown
  lastLanding: Landing | null;
  fallCause: FallCause | null;
  /** The limit crossed last (noCrash): the recovery under way and its cause, null when riding normally. */
  recovery: FallCause | null;
  steerAngle: number;  // rad, the front wheel's actual angle (lean steering + countersteer), for the view
  hit: Hit | null;     // set on a step with a hard obstacle contact (points at hitInfo, reused)
  hitInfo: Hit;        // the buffer `hit` points to
  conesHit: number;    // cones knocked over since the bike was created
  reversing: boolean;  // paddling backwards (both feet down)
  bottomRate: number;  // m/s the suspension was still closing at on its bump stop this step (0 = not bottomed)
}

export interface BikeState {
  position: Vec3;
  velocity: Vec3;
  orientation: Quat;
  angularVelocity: Vec3; // world space
  wheels: [WheelState, WheelState]; // [rear, front]
  rider: RiderState;
  engine: EngineState;
  airTime: number;       // s since the last wheel contact
  washout: number;       // s the front has been past its grip
  crashIn: number;       // s left of the last landing's hit playing out (0 = none)
  conesHit: number;      // cones knocked over so far
  brakePressure: [number, number]; // N*m acting now [rear, front], following the levers
  spinFor: number;       // s the bike has been sideways past SPIN_OUT
  recoverFor: number;    // s left of the recovery started by the last limit crossed (noCrash)
  backFor: number;       // s the back lever (rear brake / the one brake) has been held at a standstill with no gas
  paddling: boolean;     // the rider is paddling the bike backwards with his feet (no reverse gear on an MX bike)
  steerIn: number;       // the steer input the rider has worked the bars to (rate-limited input.steer)
  steadyFor: number;     // s of a steady part throttle and no rear brake (the rider's lean eases on a sliding rear then)
  throttlePrev: number;  // the throttle input last step, and its filtered rate (the posture controller's "rolling on")
  throttleTrend: number;
  /** True until the first step: wheels then spin up to the bike's speed and the gear matches it (spawned rolling). */
  fresh: boolean;
  telemetry: BikeTelemetry;
}

const newWheel = (): WheelState => ({ compression: 0, contact: false, omega: 0, load: 0, slipRatio: 0, usage: 0 });

export function createBike(position: Vec3, orientation: Quat = quatIdentity(), cfg?: BikeConfig): BikeState {
  return {
    position,
    velocity: vec(),
    orientation,
    angularVelocity: vec(),
    wheels: [newWheel(), newWheel()],
    rider: createRider(),
    engine: cfg ? createEngine(cfg) : { rpm: 1500, gear: 1, shiftTimer: 0, clutch: 0 },
    airTime: 0,
    washout: 0,
    crashIn: 0,
    conesHit: 0,
    brakePressure: [0, 0],
    spinFor: 0,
    backFor: 0,
    paddling: false,
    steadyFor: 0,
    recoverFor: 0,
    steerIn: 0,
    throttlePrev: 0,
    throttleTrend: 0,
    fresh: true,
    telemetry: {
      speed: 0, rearSlip: 0, frontLock: false, airborne: true, fallen: false,
      rpm: 1500, gear: 1, frontBrake: 0, rearBrake: 0, rearLock: false, frontWashout: false, wheelie: false,
      pitch: 0, roll: 0, sideslip: 0, landing: null, lastLanding: null, fallCause: null, recovery: null, steerAngle: 0, hit: null, hitInfo: { kind: 'tree', speed: 0, part: 'bike' }, conesHit: 0, reversing: false, bottomRate: 0,
    },
  };
}

/** Front and rear brake torque (N*m) from the levers: split levers, or the single lever split combinedBrakeFront. */
export function brakeTorques(input: InputFrame, cfg: BikeConfig): { front: number; rear: number } {
  const single = clamp(input.brake, 0, 1) * cfg.combinedBrakeTorque;
  return {
    front: Math.max(clamp(input.frontBrake ?? 0, 0, 1) * cfg.frontBrakeTorque, single * cfg.combinedBrakeFront),
    rear: Math.max(clamp(input.rearBrake ?? 0, 0, 1) * cfg.rearBrakeTorque, single * (1 - cfg.combinedBrakeFront)),
  };
}

/**
 * Front wheel angle for a steer input. Full steer asks for the tightest turn the bike does at walking pace, or a
 * steerLateralAccel turn at speed, whichever is gentler: the rider steers for a line, not a wheel angle. The same
 * input on a slippery surface, or a faster entry than the line allows, asks more than the tyres give, and the bike
 * runs wide or slides (coach points 3 and 14).
 */
export function steerAngleFor(steer: number, speed: number, cfg: BikeConfig): number {
  const v2 = speed * speed;
  const kMax = Math.min(Math.tan(cfg.maxSteer) / cfg.wheelbase, v2 > 1e-6 ? cfg.steerLateralAccel / v2 : Infinity);
  return Math.atan(clamp(steer, -1, 1) * kMax * cfg.wheelbase);
}

/** Inverse of steerAngleFor (bots and tests steer for an angle). */
export function steerInputFor(angle: number, speed: number, cfg: BikeConfig): number {
  const full = steerAngleFor(1, speed, cfg);
  return clamp(Math.tan(angle) / Math.tan(full), -1, 1);
}

/** Where along the tyre the wheel meets the ground, fore and aft of the axle, as fractions of the radius. */
const WHEEL_PROFILE = [-0.9, -0.6, -0.3, 0, 0.3, 0.6, 0.9] as const;
const EDGE_COS = Math.cos(0.26); // a contact normal more than 15 deg off the ground's own is an edge

// ---------------------------------------------------------------------------------------------------------------
// The step below runs at 240 Hz: it allocates nothing. Vectors are scalars or module-level scratch, results are
// written into reused objects (see tests/sim/alloc.test.ts).
// ---------------------------------------------------------------------------------------------------------------

/**
 * Top of the rocks, stones and bales at (x, z) that a tyre can climb (lower than `bumpMax`; taller ones are walls for
 * the body capsules), or -Infinity.
 */
function bumpTop(field: ObstacleField, n: number, x: number, z: number): number {
  let top = -Infinity;
  for (let k = 0; k < n; k++) {
    const t = field.topAt(BUMPS[k]!, x, z);
    if (t > top) top = t;
  }
  return top;
}

const BUMPS = new Int32Array(64);
/** The step's broadphase hits a tyre at (px, pz) (radius r) can climb, into BUMPS; returns how many. */
function wheelBumps(field: ObstacleField, nHits: number, bumpMax: number, px: number, pz: number, r: number): number {
  let n = 0;
  for (let k = 0; k < nHits && n < BUMPS.length; k++) {
    const i = field.hits[k]!;
    if (field.knocked[i] || field.h[i]! >= bumpMax || !KIND_FEEL[KINDS[field.kind[i]!]!].bump) continue;
    const reach = r + Math.max(field.a[i]!, field.b[i]!);
    if (Math.abs(field.x[i]! - px) > reach || Math.abs(field.z[i]! - pz) > reach) continue;
    BUMPS[n++] = i;
  }
  return n;
}

/** Terrain sample without allocating (sampleInto when the terrain has it). */
function sampleTo(terrain: Terrain, x: number, z: number, out: TerrainSample): void {
  if (terrain.sampleInto) { terrain.sampleInto(x, z, out); return; }
  const g = terrain.sample(x, z);
  out.height = g.height; out.surface = g.surface;
  out.normal.x = g.normal.x; out.normal.y = g.normal.y; out.normal.z = g.normal.z;
}

// Scratch for batched height probes and for passing doubles without boxing them (arguments to calls V8 does not
// inline are boxed: one allocation each).
const XS = new Float64Array(16), ZS = new Float64Array(16), HS = new Float64Array(16);
const WGIN = new Float64Array(6);
const PAIR = new Float64Array(16);
/** Heights of XS/ZS[0..n) into HS. */
function heights(terrain: Terrain, n: number): void {
  if (terrain.heightsInto) { terrain.heightsInto(XS, ZS, n, HS); return; }
  for (let i = 0; i < n; i++) HS[i] = terrain.heightAt ? terrain.heightAt(XS[i]!, ZS[i]!) : terrain.sample(XS[i]!, ZS[i]!).height;
}

/** Where a wheel meets the ground (wheelGround's result, reused). */
interface WheelGround { height: number; nx: number; ny: number; nz: number; surface: Surface; edge: boolean; ex: number; ey: number; ez: number }
const WG: WheelGround = { height: 0, nx: 0, ny: 1, nz: 0, surface: 'clay', edge: false, ex: 0, ey: 0, ez: 0 };
const SAMPLE: TerrainSample = { height: 0, normal: { x: 0, y: 1, z: 0 }, surface: 'clay' };

/**
 * The ground as a round wheel feels it: the highest point of the terrain under the tyre's circle (not just the point
 * under the axle), so a log or a step is climbed by the front of the tyre and pushes back, instead of teleporting the
 * wheel up when the axle passes over its edge. On smooth ground this is the usual sample. Result in WG.
 */
function wheelGround(terrain: Terrain, field: ObstacleField | null, nHits: number): void {
  const px = WGIN[0]!, pz = WGIN[1]!, fx = WGIN[2]!, fz = WGIN[3]!, r = WGIN[4]!, bumpMax = WGIN[5]!;
  const fl = Math.sqrt(fx * fx + fz * fz);
  const hx = fl > 1e-6 ? fx / fl : 0, hz = fl > 1e-6 ? fz / fl : 1;
  const nb = field ? wheelBumps(field, nHits, bumpMax, px, pz, r) : 0;
  const np = WHEEL_PROFILE.length;
  for (let k = 0; k < np; k++) { const dx = WHEEL_PROFILE[k]! * r; XS[k] = px + hx * dx; ZS[k] = pz + hz * dx; }
  heights(terrain, np);
  let bestH = -Infinity, bestDx = 0, onObstacle = false;
  for (let k = 0; k < np; k++) {
    const dx = WHEEL_PROFILE[k]! * r;
    const g0 = HS[k]!;
    const b0 = nb > 0 ? bumpTop(field!, nb, XS[k]!, ZS[k]!) : -Infinity;
    const h = Math.max(g0, b0) - (r - Math.sqrt(r * r - dx * dx));
    if (h > bestH + 1e-9) { bestH = h; bestDx = dx; onObstacle = b0 > g0; }
  }
  const bx = px + hx * bestDx, bz = pz + hz * bestDx;
  sampleTo(terrain, bx, bz, SAMPLE);
  let gh = SAMPLE.height, gnx = SAMPLE.normal.x, gny = SAMPLE.normal.y, gnz = SAMPLE.normal.z;
  let surface = SAMPLE.surface;
  if (onObstacle) {
    // On a rock or a bale: its own surface normal (numerical gradient of its top) and a hard, slippery surface.
    const e = 0.03;
    const f = field!;
    gh = bumpTop(f, nb, bx, bz);
    const gx = (bumpTop(f, nb, bx + e, bz) - bumpTop(f, nb, bx - e, bz)) / (2 * e);
    const gz = (bumpTop(f, nb, bx, bz + e) - bumpTop(f, nb, bx, bz - e)) / (2 * e);
    const ax = Number.isFinite(gx) ? -gx : 0, az = Number.isFinite(gz) ? -gz : 0, l = Math.sqrt(ax * ax + 1 * 1 + az * az);
    gnx = ax / l; gny = 1 / l; gnz = az / l;
    surface = 'roots';
  }
  WG.surface = surface; WG.edge = false;
  if (bestDx === 0) { WG.height = gh; WG.nx = gnx; WG.ny = gny; WG.nz = gnz; return; }
  // Normal from the touching point toward the wheel centre; use it only where the ground itself is not that steep.
  const lift = Math.sqrt(r * r - bestDx * bestDx);
  let ox = -bestDx * hx, oy = lift, oz = -bestDx * hz;
  const ol = Math.sqrt(ox * ox + oy * oy + oz * oz); ox /= ol; oy /= ol; oz /= ol;
  WG.height = bestH;
  if (ox * gnx + oy * gny + oz * gnz > EDGE_COS) { WG.nx = gnx; WG.ny = gny; WG.nz = gnz; return; }
  // The tyre wraps round an edge (a log, a step) rather than touching it at a point: halfway between the two normals.
  let sx = ox + gnx, sy = oy + gny, sz = oz + gnz;
  const sl = Math.sqrt(sx * sx + sy * sy + sz * sz) || 1; sx /= sl; sy /= sl; sz /= sl;
  WG.nx = sx; WG.ny = sy; WG.nz = sz;
  WG.edge = true; WG.ex = bx; WG.ey = gh; WG.ez = bz;
}

/** Either wheel touched the ground last step. */
const grounded0 = (s: BikeState) => s.wheels[0].contact || s.wheels[1].contact;

/** Wheels spin at the ground speed and the gearbox picks the gear for it, as if the bike had been rolling. */
function spinUp(s: BikeState, cfg: BikeConfig): void {
  const v = dot(s.velocity, rotate(s.orientation, vec(0, 0, 1)));
  for (const w of s.wheels) w.omega = v / cfg.wheelRadius;
  const wheelRpm = (Math.abs(v) / cfg.wheelRadius) * (60 / (2 * Math.PI));
  let gear = 1;
  while (gear < cfg.gearRatios.length && wheelRpm * cfg.gearRatios[gear - 1]! > cfg.shiftUpRpm) gear++;
  s.engine = { ...createEngine(cfg), gear };
  s.fresh = false;
}

/** One wheel's ground contact this step (reused). */
interface Contact { active: boolean; nx: number; ny: number; nz: number; surface: Surface; px: number; py: number; pz: number }
const newContact = (): Contact => ({ active: false, nx: 0, ny: 1, nz: 0, surface: 'clay', px: 0, py: 0, pz: 0 });
const CONTACTS: [Contact, Contact] = [newContact(), newContact()];

/** What a fallen rider does: nothing (hands off, the engine idles, the bike lies where it goes). */
const FALLEN_INPUT: InputFrame = { throttle: 0, brake: 0, steer: 0, body: 0, standing: false };

const DRIVE: DriveOutput = { torque: 0, inertia: 0 };
const TYRE: TyreParams = { slipPeak: 0, anglePeak: 0, shape: 0 };
const SPIN: SpinInput = { omega: 0, vLong: 0, tanSlipAngle: 0, muN: 0, drive: 0, brake: 0, inertia: 0, radius: 0, slipFloor: 0, tyre: TYRE };
const SPUN: SpinResult = { omega: 0, fx: 0, fy: 0, usage: 0, slipRatio: 0 };

// Force and torque accumulators of the step (an object with double fields: module-level `let` doubles are boxed on every write).
const A = { fx: 0.5, fy: 0.5, fz: 0.5, tx: 0.5, ty: 0.5, tz: 0.5, bottom: 0.5 }; // (initialised as doubles: double fields are stored unboxed)
/** Adds force PAIR[3..6) at arm PAIR[0..3) (from the COM). */
function applyAt(): void {
  const ax = PAIR[0]!, ay = PAIR[1]!, az = PAIR[2]!, x = PAIR[3]!, y = PAIR[4]!, z = PAIR[5]!;
  A.fx += x; A.fy += y; A.fz += z;
  A.tx += ay * z - az * y; A.ty += az * x - ax * z; A.tz += ax * y - ay * x;
}
/**
 * Penalty spring with viscous-capped Coulomb friction for a body point PAIR[6] deep inside a surface with normal
 * PAIR[7..10), moving at PAIR[10..13); applied at arm PAIR[0..3) (from the COM).
 */
function contactAt(cfg: BikeConfig): void {
  const nx = PAIR[7]!, ny = PAIR[8]!, nz = PAIR[9]!, depth = PAIR[6]!, vx = PAIR[10]!, vy = PAIR[11]!, vz = PAIR[12]!;
  const vn = vx * nx + vy * ny + vz * nz;
  const normalForce = Math.max(0, cfg.chassisStiffness * depth - cfg.chassisDamping * vn);
  const tx = vx - nx * vn, ty = vy - ny * vn, tz = vz - nz * vn;
  const vtLen = Math.sqrt(tx * tx + ty * ty + tz * tz);
  // Viscous below the Coulomb limit: an impulse-style clamp overshoots on points with small effective mass.
  const k = vtLen > 1e-9 ? -Math.min(cfg.chassisFriction * normalForce, cfg.chassisDamping * vtLen) / vtLen : 0;
  PAIR[3] = nx * normalForce + tx * k; PAIR[4] = ny * normalForce + ty * k; PAIR[5] = nz * normalForce + tz * k;
  applyAt();
}

export function stepBike(s: BikeState, riderInput: InputFrame, terrain: Terrain, cfg: BikeConfig, dt: number): void {
  const input = s.telemetry.fallen ? FALLEN_INPUT : riderInput;
  const first = s.fresh;
  if (first) spinUp(s, cfg);
  const P = s.position, V = s.velocity, W = s.angularVelocity, Q = s.orientation;
  quatToMatrix(Q, ROT);
  // Bike axes in the world: left (x), up (y), forward (z) are the matrix columns.
  const lx = ROT[0]!, ly = ROT[3]!, lz = ROT[6]!;
  const ux = ROT[1]!, uy = ROT[4]!, uz = ROT[7]!;
  const fx = ROT[2]!, fy = ROT[5]!, fz = ROT[8]!;
  const speed = V.x * fx + V.y * fy + V.z * fz;
  const roll = -Math.asin(clamp(ly, -1, 1));
  const rollRate = -(W.x * fx + W.y * fy + W.z * fz);

  const bodyPrev = s.rider.body;
  // Paddling backwards: at a standstill, the back lever (S / B / REAR: the rear brake, or simplified mode's one brake)
  // held PADDLE_HOLD with no gas puts the rider's feet down to push the bike back, steerable, up to PADDLE_SPEED.
  // Above walking pace the same lever is only a brake. Gas, letting go of the lever or a fall ends it.
  const back = Math.max(input.rearBrake ?? 0, input.brake, input.assist ? (input.frontBrake ?? 0) : 0);
  const wantsBack = back > 0.5 && input.throttle < 0.05 && !s.telemetry.fallen;
  s.backFor = wantsBack && (s.paddling || Math.abs(speed) < PADDLE_START) ? s.backFor + dt : 0;
  s.paddling = wantsBack && s.backFor >= PADDLE_HOLD;
  const paddling = s.paddling;
  // What the rider reads for his posture (only an auto-posture ride looks at the ground ahead).
  s.throttleTrend += ((input.throttle - s.throttlePrev) / dt - s.throttleTrend) * Math.min(1, dt / 0.15);
  s.throttlePrev = input.throttle;
  const cues = input.autoPosture ? CUES : null;
  if (cues) {
    cues.speed = speed; cues.roll = roll; cues.throttle = input.throttle; cues.throttleTrend = s.throttleTrend;
    cues.curvature = Math.tan(steerAngleFor(input.steer, speed, cfg)) / cfg.wheelbase;
    cues.airborne = !grounded0(s);
    const hl = Math.sqrt(fx * fx + fz * fz) || 1, hx = fx / hl, hz = fz / hl;
    for (let k = 0; k < PROBE_AHEAD.length; k++) { XS[k] = P.x + hx * PROBE_AHEAD[k]!; ZS[k] = P.z + hz * PROBE_AHEAD[k]!; }
    heights(terrain, PROBE_AHEAD.length);
    let rough = 0;
    for (let k = 1; k < PROBE_AHEAD.length - 1; k++) rough = Math.max(rough, Math.abs(HS[k - 1]! - 2 * HS[k]! + HS[k + 1]!));
    cues.rough = rough;
    let face = 0;
    for (let k = 1; k < PROBE_AHEAD.length; k++) face = Math.max(face, (HS[k]! - HS[k - 1]!) / (PROBE_AHEAD[k]! - PROBE_AHEAD[k - 1]!));
    cues.face = face;
  }
  stepRider(s.rider, input, speed, roll, cfg, dt, paddling, cues); // paddling: feet on the ground (the view reads reversing)
  // COM relative to the bike centre (world): the rider's height and fore-aft position in the bike frame at his
  // posture's place (standing he can reach further fore and aft), plus his sprung deviation (riderMass of it), plus, standing,
  // the body staying more upright than the leaned bike (riderUpright): the COM sits outside the bike's plane.
  const mu = cfg.riderMass / cfg.mass, rd = s.rider;
  const comY = cfg.seatedComHeight + (cfg.standingComHeight - cfg.seatedComHeight) * rd.stand + mu * rd.dy;
  const comZ = rd.body * cfg.riderComShift * (1 + cfg.standBodyReach * rd.stand * (rd.body > 0 ? 1 : 0)) + mu * rd.dz;
  const comX = -mu * cfg.riderUpright * rd.stand * Math.sin(roll);
  const cx = lx * comX + ux * comY + fx * comZ, cy = ly * comX + uy * comY + fy * comZ, cz = lz * comX + uz * comY + fz * comZ;
  const dampingScale = legDamping(s.rider, cfg);
  // Brake pressure builds in the lines and calipers (brakeRise), so even a slammed trigger takes ~0.1 s to bite fully;
  // it lets go faster (brakeFall).
  const single = paddling ? 0 : clamp(input.brake, 0, 1) * cfg.combinedBrakeTorque;
  const askFront = paddling ? 0 : Math.max(clamp(input.frontBrake ?? 0, 0, 1) * cfg.frontBrakeTorque, single * cfg.combinedBrakeFront);
  const askRear = paddling ? 0 : Math.max(clamp(input.rearBrake ?? 0, 0, 1) * cfg.rearBrakeTorque, single * (1 - cfg.combinedBrakeFront));
  const bp = s.brakePressure;
  bp[0] = bp[0] + (askRear - bp[0]) * Math.min(1, dt / (askRear > bp[0] ? cfg.brakeRise : cfg.brakeFall));
  bp[1] = bp[1] + (askFront - bp[1]) * Math.min(1, dt / (askFront > bp[1] ? cfg.brakeRise : cfg.brakeFall));
  const leverRear = s.brakePressure[0], leverFront = s.brakePressure[1];
  // The rider asks for a line (steerAngleFor); above walking pace the bike turns by leaning, so the front wheel angle
  // follows the lean the bike actually has (a leaned bike steers itself into the turn), and the rider's balance
  // (below) leans it toward the line. At walking pace the bars steer directly.
  // Simplified mode: full steer asks for a gentler line at speed (ASSIST.lateralAccel), so a held key cannot lay the bike down.
  // The bars move at a rider's pace at speed (a held key snapped them to full lock: the bike flicked in at
  // 115-120 deg/s of roll and the ride looked jerky, owner 2026-10-05); at walking pace they turn quickly.
  const steerRate = STEER_RATE * (1 + 3 * clamp(1 - Math.abs(speed) / 5, 0, 1)) * dt;
  s.steerIn = input.assist ? clamp(clamp(input.steer, -1, 1), s.steerIn - steerRate, s.steerIn + steerRate) : clamp(input.steer, -1, 1);
  const steerIn = s.steerIn;
  const askedAngle = input.assist
    ? Math.atan(steerIn * Math.min(Math.tan(cfg.maxSteer) / cfg.wheelbase, speed * speed > 1e-6 ? ASSIST.lateralAccel / (speed * speed) : Infinity) * cfg.wheelbase)
    : steerAngleFor(steerIn, speed, cfg);
  const leanCurvature = speed * speed > 1e-6 ? (GRAVITY * Math.tan(clamp(roll, -1.2, 1.2))) / (speed * speed) : 0;
  const leanAngle = Math.atan(clamp(leanCurvature * cfg.wheelbase, -Math.tan(cfg.maxSteer), Math.tan(cfg.maxSteer)));
  const byLean = clamp((Math.abs(speed) - 2) / 3, 0, 1);
  // Lean for the asked line; the foot out lets the bike lean a little further.
  // Simplified mode leans less, and stands the bike up for a jump face (nose rising off the ground).
  const pitchNow0 = Math.asin(clamp(fy, -1, 1));
  // Real mode: a rider feels a tyre start to push or step out and stops asking for more lean (he runs wide rather than
  // lies the bike down): the lean he goes for shrinks as the busier tyre's grip use (last step) passes FRONT_PUSH.
  // (The rear counts once a part throttle has been held steady a while: a stab of gas or a locked rear brake, and the
  // slide it leaves, are meant to step it out and turn the bike, coach points 3 and 4.)
  // The leg out only props the bike where the foot can reach the ground at walking-to-trotting pace; faster it is
  // just where the leg is held (MX: the inside leg is out through every seated corner).
  const footDown = s.rider.foot * clamp(1 - Math.abs(speed) / cfg.footMaxSpeed, 0, 1);
  const steady = input.throttle <= STEADY_THROTTLE && (input.rearBrake ?? 0) < 0.05 && input.brake < 0.05;
  s.steadyFor = steady ? s.steadyFor + dt : 0;
  const rearCounts = s.wheels[0].contact && s.steadyFor > STEADY_TIME;
  const frontUse = Math.max(s.wheels[1].contact ? s.wheels[1].usage : 0, rearCounts ? s.wheels[0].usage : 0);
  const pushBack = clamp((FRONT_PUSH[1] - frontUse) / (FRONT_PUSH[1] - FRONT_PUSH[0]), FRONT_PUSH_MIN, 1);
  const maxLean = input.assist ? ASSIST.maxLean * clamp(1 - (pitchNow0 - 0.08) / 0.15, 0.25, 1)
    : (cfg.maxLean + cfg.footLeanBonus * footDown) * pushBack;
  const targetRoll = clamp(Math.atan((speed * Math.abs(speed) * Math.tan(askedAngle)) / (cfg.wheelbase * GRAVITY)), -maxLean, maxLean);
  // Countersteer: leaned further than wanted, the rider steers further into the lean, which stands the bike up
  // (and the other way round). Needs some speed; the same lean error needs less bar the faster the bike goes.
  const counterGain = clamp((Math.abs(speed) - 1) / 2, 0, 1) * Math.min(1, 9 / Math.max(1e-6, speed * speed));
  const counter = counterGain * cfg.countersteer * (roll - targetRoll);
  // Opposite lock: with the rear stepped out the bars follow the direction of travel (the fork's trail swings the front
  // wheel that way, and the rider lets it), so a slide carries the bike round instead of tipping it over.
  const vhs = Math.sqrt(V.x * V.x + V.z * V.z);
  const slide0 = vhs > 2 ? Math.atan2(fx * V.z - fz * V.x, fx * V.x + fz * V.z) : 0;
  // (A dead band: the few degrees a landing or a line correction carries are not a slide.)
  const slideAngle = Math.sign(slide0) * Math.max(0, Math.abs(slide0) - SLIDE_LOCK_FROM);
  const steerAngle = grounded0(s) && !s.telemetry.fallen // (a rider who is down has let go of the bars)
    ? clamp(byLean * leanAngle + (1 - byLean) * askedAngle + counter - SLIDE_LOCK * slideAngle, -cfg.maxSteer, cfg.maxSteer)
    : askedAngle;

  A.fx = 0; A.fy = -cfg.mass * GRAVITY; A.fz = 0; A.tx = 0; A.ty = 0; A.tz = 0; A.bottom = 0;
  const reach = cfg.suspensionRest + cfg.wheelRadius;
  // Suspension rays point straight down, so they only model the wheels while the bike is fairly upright.
  // Once fallen they stay off (fallen is sticky until reset): re-enabling a tilted ray hits the bump stop and launches the bike.
  const wheelsCanTouch = !s.telemetry.fallen && uy >= cfg.fallUpDot;

  // Obstacles near the bike (broadphase once per step; the wheels and the body both read this list).
  const field = terrain.field ?? null;
  const nHits = field ? field.query(P.x, P.z, OBSTACLE_QUERY + field.maxReach) : 0;

  // Pass 1: suspension. Loads first, so the drivetrain knows whether a wheel is down.
  for (let i = 0; i < 2; i++) {
    const wheel = s.wheels[i]!;
    const c = CONTACTS[i]!;
    c.active = false;
    const mz = (i === 1 ? 0.5 : -0.5) * cfg.wheelbase;
    const px = P.x + fx * mz, py = P.y + fy * mz, pz = P.z + fz * mz;
    WGIN[0] = px; WGIN[1] = pz; WGIN[2] = fx; WGIN[3] = fz; WGIN[4] = cfg.wheelRadius; WGIN[5] = cfg.collision.minBodyHeight;
    wheelGround(terrain, field, nHits);
    // Distance from the axle to the ground along the bike's own down axis (the fork line), so a leaned or pitched
    // bike touches where its tyre does: under the COM line, not under the axle.
    const gap = ((py - WG.height) * WG.ny) / Math.max(0.2, ux * WG.nx + uy * WG.ny + uz * WG.nz);
    const previous = wheel.compression;
    wheel.contact = wheelsCanTouch && gap < reach;
    wheel.compression = wheel.contact ? Math.max(0, reach - gap) : 0;
    wheel.load = 0;
    if (!wheel.contact) continue;
    const rate = first ? 0 : (wheel.compression - previous) / dt; // a spawned bike has no history to damp
    const travel = i === 1 ? cfg.forkTravel : cfg.shockTravel;
    const overTravel = Math.max(0, wheel.compression - travel);
    if (overTravel > 0 && rate > A.bottom) A.bottom = rate; // still closing on the bump stop
    // Compression: digressive damping, firm for bumps, it gives way on a big hit so a hard landing uses all the
    // travel. Rebound (extending) has its own, slower damping. A rising-rate spring and a hydraulic bump stop at the
    // end of the travel.
    const damped = rate > cfg.dampingKnee ? cfg.dampingKnee + (rate - cfg.dampingKnee) * cfg.dampingHighSpeed : rate;
    const cmp = wheel.compression, ramp = Math.max(0, cmp - cfg.progressionStart * travel);
    wheel.load = Math.max(0,
      cfg.springRate * (i === 1 ? cfg.forkSpringScale : 1) * cmp + cfg.springProgression * ramp * ramp * ramp + cfg.bumpStopStiffness * overTravel
      + (overTravel > 0 ? cfg.bumpStopDamping * rate : 0) + (rate >= 0 ? cfg.damping : cfg.reboundDamping) * dampingScale * damped);
    c.active = true;
    c.nx = WG.nx; c.ny = WG.ny; c.nz = WG.nz; c.surface = WG.surface;
    if (WG.edge) { c.px = WG.ex; c.py = WG.ey; c.pz = WG.ez; } else { c.px = px - ux * gap; c.py = py - uy * gap; c.pz = pz - uz * gap; }
  }
  const rearC = CONTACTS[0], frontC = CONTACTS[1];
  const grounded = rearC.active || frontC.active;
  // Rider instinct on a wheelie: as the front climbs toward the balance point the rider feathers the clutch and rolls
  // off, so a held throttle holds the wheelie instead of looping it. Beginner mode rolls off earlier.
  let throttle = input.throttle;
  if (rearC.active && !frontC.active) {
    const lift = Math.asin(clamp(fx * rearC.nx + fy * rearC.ny + fz * rearC.nz, -1, 1)); // nose above the ground under the rear
    const band = input.assist ? (input.trick === 1 ? ASSIST.trickBand : ASSIST.wheelieBand) : input.autoFoot ? cfg.wheelieRollOffBeginner : cfg.wheelieRollOff;
    throttle *= clamp((band[1] - lift) / (band[1] - band[0]), 0, 1);
  }
  // Leaned over with the rear already spinning well past its grip, a rider rolls the gas back (nobody holds it wide open
  // at 45 deg): a burst still steps the rear out (coach point 3), a held-open throttle no longer highsides the bike.
  // Simplified only: Real leaves the throttle to the player, so how much the rear steps out is how much gas is held
  // (owner 2026-10-04: a powerslide is a controlled technique, too much gas spins the bike round).
  if (input.assist) {
    const leanedFor = clamp((Math.abs(roll) - LEAN_SPIN[0]) / (LEAN_SPIN[1] - LEAN_SPIN[0]), 0, 1);
    const spinning = clamp((s.telemetry.rearSlip - REAR_SPIN[0]) / (REAR_SPIN[1] - REAR_SPIN[0]), 0, 1);
    throttle *= 1 - REAR_SPIN_CUT * leanedFor * spinning;
    // Sideways past slideRollOff[0] the assist eases the gas rather than spinning the bike round.
    const sideways = s.telemetry.sideslip;
    if (sideways > cfg.slideRollOff[0]) throttle *= clamp((cfg.slideRollOff[1] - sideways) / (cfg.slideRollOff[1] - cfg.slideRollOff[0]), 0, 1);
  }
  // The same instinct on a stoppie (Real; Simplified's assist holds its own): as the tail climbs toward the balance
  // point the rider eases the front lever, so a held lever holds the stoppie; momentum or weight forward can still flip it.
  let frontLever = leverFront;
  if (frontC.active && !rearC.active && !input.assist) {
    const tail = -Math.asin(clamp(fx * frontC.nx + fy * frontC.ny + fz * frontC.nz, -1, 1));
    frontLever *= 1 - STOPPIE_EASE * clamp((tail - cfg.stoppieRollOff[0]) / (cfg.stoppieRollOff[1] - cfg.stoppieRollOff[0]), 0, 1);
  }
  // Recovering (noCrash): the rider rolls off while he gets the bike back under himself (the speed drops a little).
  if (s.recoverFor > 0) throttle *= 1 - 0.7 * (s.recoverFor / cfg.recoverTime);
  stepDrivetrain(s.engine, throttle, leverRear / cfg.rearBrakeTorque, s.wheels[0].omega, grounded, cfg, dt, speed / cfg.wheelRadius, DRIVE);

  // Pass 2: tyres and wheel spin.
  TYRE.slipPeak = cfg.slipPeak; TYRE.anglePeak = cfg.slipAnglePeak; TYRE.shape = cfg.tyreShape;
  SPIN.radius = cfg.wheelRadius; SPIN.slipFloor = cfg.slipFloor;
  let spinMomentum = 0; // kg*m^2/s about the bike's left axis, both wheels
  let spinReaction = 0; // N*m about the left axis the wheels push back on the chassis
  let frontLateralUsage = 0;
  let frontSliding = false;
  const cosS = Math.cos(steerAngle), sinS = Math.sin(steerAngle);
  for (let i = 0; i < 2; i++) {
    const wheel = s.wheels[i]!;
    const c = CONTACTS[i]!;
    const isFront = i === 1;
    const inertia = isFront ? cfg.frontWheelInertia : cfg.rearWheelInertia + DRIVE.inertia;
    const omega0 = wheel.omega;
    SPIN.omega = omega0; SPIN.drive = isFront ? 0 : DRIVE.torque; SPIN.brake = isFront ? frontLever : leverRear; SPIN.inertia = inertia;
    if (!c.active) {
      SPIN.vLong = 0; SPIN.tanSlipAngle = 0; SPIN.muN = 0;
      stepWheelSpin(SPIN, dt, SPUN);
      wheel.omega = SPUN.omega;
      wheel.slipRatio = 0;
      wheel.usage = 0;
    } else {
      const nx = c.nx, ny = c.ny, nz = c.nz;
      // Heading: the front turned by the steer angle about the bike's up axis (fwd cos + left sin).
      const hx = isFront ? fx * cosS + lx * sinS : fx, hy = isFront ? fy * cosS + ly * sinS : fy, hz = isFront ? fz * cosS + lz * sinS : fz;
      const hn = hx * nx + hy * ny + hz * nz;
      let ex = hx - nx * hn, ey = hy - ny * hn, ez = hz - nz * hn;
      const el = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1; ex /= el; ey /= el; ez /= el;
      // side = n x f
      const sx = ny * ez - nz * ey, sy = nz * ex - nx * ez, sz = nx * ey - ny * ex;
      const ax = c.px - P.x, ay = c.py - P.y, az = c.pz - P.z;
      const vpx = V.x + (W.y * az - W.z * ay), vpy = V.y + (W.z * ax - W.x * az), vpz = V.z + (W.x * ay - W.y * ax);
      const vLong = vpx * ex + vpy * ey + vpz * ez;
      const vLat = vpx * sx + vpy * sy + vpz * sz;
      const feel = SURFACE_FEEL[c.surface];

      // Rut walls: the tyre sits across the groove, so the walls push it toward the rut floor.
      let wall = 0;
      let rutLoss = 1;
      if (terrain.rutDepth) {
        const dA = terrain.rutDepth(c.px + sx * RUT_PROBE, c.pz + sz * RUT_PROBE), dB = terrain.rutDepth(c.px - sx * RUT_PROBE, c.pz - sz * RUT_PROBE);
        const gradient = (dA - dB) / (2 * RUT_PROBE); // + = deeper on the `side` side
        wall = wheel.load * clamp(cfg.rutWallGain * gradient, -0.8, 0.8); // the wall pushes at most like a steep bank
        // Climbing out across a deep wall: the front tyre is on the edge and has little lateral grip.
        const climbing = isFront && vLat * gradient < 0 ? Math.abs(vLat) / (Math.abs(vLong) + 1) : 0;
        rutLoss = 1 / (1 + cfg.rutGripLoss * Math.max(dA, dB) * Math.abs(gradient) * climbing * 10);
      }
      const mu = cfg.grip * SURFACE_GRIP[c.surface] * (isFront ? rutLoss : 1);
      SPIN.vLong = vLong; SPIN.tanSlipAngle = vLat / (Math.abs(vLong) + cfg.slipFloor); SPIN.muN = mu * wheel.load;
      stepWheelSpin(SPIN, dt, SPUN);
      wheel.omega = SPUN.omega;
      wheel.slipRatio = SPUN.slipRatio;
      wheel.usage = SPUN.usage;

      // Rolling resistance, and soft ground that ploughs harder the more load it carries.
      const staticLoad = (cfg.mass * GRAVITY) / 2;
      const resist = wheel.load * (cfg.rollingResistance + feel.roll + feel.sink * (wheel.load / staticLoad));
      const rolling = -resist * Math.tanh(vLong / 0.5);
      // Loose ground deflects the front: a fixed noise field, stronger with more weight on the front.
      const wander = isFront && feel.wander > 0
        ? feel.wander * wheel.load * valueNoise(WANDER_SEED, c.px / WANDER_SCALE, c.pz / WANDER_SCALE) * Math.min(1, Math.abs(vLong) / 3)
        : 0;
      const along = SPUN.fx + rolling, across = SPUN.fy + wall + wander, load = wheel.load;
      PAIR[0] = ax - cx; PAIR[1] = ay - cy; PAIR[2] = az - cz;
      PAIR[3] = nx * load + ex * along + sx * across; PAIR[4] = ny * load + ey * along + sy * across; PAIR[5] = nz * load + ez * along + sz * across;
      applyAt();
      if (isFront) {
        const tanA = Math.abs(vLat) / (Math.abs(vLong) + cfg.slipFloor);
        frontLateralUsage = tanA / Math.tan(cfg.slipAnglePeak) / Math.max(1e-3, rutLoss);
        frontSliding = SPUN.usage > 1.15 && Math.abs(vLong) > 1;
      }
    }
    // Spinning the wheel up or down twists the chassis the other way (the whole point of throttle / rear brake in the air).
    spinReaction -= (inertia * (wheel.omega - omega0)) / dt;
    spinMomentum += inertia * wheel.omega;
  }
  A.tx += lx * spinReaction; A.ty += ly * spinReaction; A.tz += lz * spinReaction;
  // Gyroscopic reaction of the spinning wheels to the chassis turning: - W x (left * spinMomentum).
  const gx = lx * spinMomentum, gy = ly * spinMomentum, gz = lz * spinMomentum;
  A.tx -= W.y * gz - W.z * gy; A.ty -= W.z * gx - W.x * gz; A.tz -= W.x * gy - W.y * gx;

  // Chassis contact: stiff penalty springs with friction at a few body points, so a fallen bike lies down.
  let bodyOnGround = 0;
  const nc = CHASSIS_POINTS.length;
  for (let k = 0; k < nc; k++) {
    const q = CHASSIS_POINTS[k]!;
    XS[k] = P.x + ROT[0]! * q.x + ROT[1]! * q.y + ROT[2]! * q.z; ZS[k] = P.z + ROT[6]! * q.x + ROT[7]! * q.y + ROT[8]! * q.z;
  }
  heights(terrain, nc);
  for (let k = 0; k < nc; k++) {
    const q = CHASSIS_POINTS[k]!;
    const ax = ROT[0]! * q.x + ROT[1]! * q.y + ROT[2]! * q.z, ay = ROT[3]! * q.x + ROT[4]! * q.y + ROT[5]! * q.z, az = ROT[6]! * q.x + ROT[7]! * q.y + ROT[8]! * q.z;
    const px = P.x + ax, py = P.y + ay, pz = P.z + az;
    if (HS[k]! <= py) continue; // cheap reject before the normal
    sampleTo(terrain, px, pz, SAMPLE);
    const depth = SAMPLE.height - py;
    if (depth <= 0) continue;
    bodyOnGround++;
    PAIR[0] = ax - cx; PAIR[1] = ay - cy; PAIR[2] = az - cz; PAIR[6] = depth;
    PAIR[7] = SAMPLE.normal.x; PAIR[8] = SAMPLE.normal.y; PAIR[9] = SAMPLE.normal.z;
    PAIR[10] = V.x + (W.y * az - W.z * ay); PAIR[11] = V.y + (W.z * ax - W.x * az); PAIR[12] = V.z + (W.x * ay - W.y * ax);
    contactAt(cfg);
  }
  // In the air: neither a wheel nor the bike's body touches the ground (a crashed bike lying down is not flying).
  const airborne = !grounded && bodyOnGround === 0;

  // Obstacles: capsules for the chassis, the rider and the bars against trees, rocks, bales, poles and props.
  let hitNow = false;
  if (field && nHits > 0) {
    collide(field, nHits, ROT, P.x, P.y, P.z, V.x, V.y, V.z, W.x, W.y, W.z, cx, cy, cz, s.rider.stand, cfg.collision, HIT);
    A.fx += HIT.fx; A.fy += HIT.fy; A.fz += HIT.fz; A.tx += HIT.tx; A.ty += HIT.ty; A.tz += HIT.tz;
    s.conesHit += HIT.cones;
    if (HIT.impactKind >= 0 && HIT.impact > cfg.collision.reportSpeed) {
      const h = s.telemetry.hitInfo;
      h.kind = KINDS[HIT.impactKind]!; h.speed = HIT.impact; h.part = HIT.impactPart === 1 ? 'rider' : HIT.impactPart === 2 ? 'bars' : 'bike';
      hitNow = true;
    }
  } else if (!field && terrain.obstacles) {
    // Terrains without an obstacle field (tests): plain trunks (vertical cylinders) against a few body points.
    const obstacles = terrain.obstacles(P.x, P.z, OBSTACLE_REACH);
    for (let o = 0; o < obstacles.length; o++) {
      const ob = obstacles[o]!;
      for (let k = 0; k < OBSTACLE_POINTS.length; k++) {
        const q = OBSTACLE_POINTS[k]!;
        const ax = ROT[0]! * q.x + ROT[1]! * q.y + ROT[2]! * q.z, ay = ROT[3]! * q.x + ROT[4]! * q.y + ROT[5]! * q.z, az = ROT[6]! * q.x + ROT[7]! * q.y + ROT[8]! * q.z;
        const dx = P.x + ax - ob.x, dz = P.z + az - ob.z, dist = Math.sqrt(dx * dx + dz * dz);
        const depth = ob.radius - dist;
        if (depth <= 0) continue;
        const nx = dist > 1e-6 ? dx / dist : 1, nz = dist > 1e-6 ? dz / dist : 0;
        PAIR[0] = ax - cx; PAIR[1] = ay - cy; PAIR[2] = az - cz; PAIR[6] = depth; PAIR[7] = nx; PAIR[8] = 0; PAIR[9] = nz;
        PAIR[10] = V.x + (W.y * az - W.z * ay); PAIR[11] = V.y + (W.z * ax - W.x * az); PAIR[12] = V.z + (W.x * ay - W.y * ax);
        contactAt(cfg);
      }
    }
  }

  // Paddling: the feet push the bike back along its heading toward -PADDLE_SPEED.
  if (paddling && grounded) {
    const push = clamp(PADDLE_GAIN * (-PADDLE_SPEED - speed), -PADDLE_FORCE, PADDLE_FORCE);
    A.fx += fx * push; A.fy += fy * push; A.fz += fz * push;
  }

  // Air drag on the rider + bike.
  const vmag = Math.sqrt(V.x * V.x + V.y * V.y + V.z * V.z);
  A.fx -= cfg.aeroDrag * vmag * V.x; A.fy -= cfg.aeroDrag * vmag * V.y; A.fz -= cfg.aeroDrag * vmag * V.z;

  const rearW = s.wheels[0], frontW = s.wheels[1];
  const lowSpeed = clamp(1 - Math.abs(speed) / cfg.footMaxSpeed, 0, 1);
  if (airborne) {
    // The rider's body keeps the bike level in the air: a weaker roll PD toward upright.
    // Whip (owner list 10-16): left/right in the air throws the bike sideways, laid over and yawed together, through
    // torques (so it builds with inertia and the size follows how long and how early the input is held); the opposite
    // input brings it back, and nothing snaps it straight: the rider has to time the recovery before the landing.
    const whipIn = clamp(input.steer, -1, 1);
    const early = s.airTime < cfg.whipEarly ? cfg.whipEarlyGain : 1; // thrown off the lip it goes further
    const tAir = input.assist
      ? ASSIST.airRollKp * (whipIn * cfg.whipRollMax - roll) - ASSIST.airRollKd * rollRate
      : cfg.airRollKp * (whipIn * cfg.whipRollMax - roll) - cfg.airRollKd * rollRate;
    A.tx -= fx * tAir; A.ty -= fy * tAir; A.tz -= fz * tAir;
    A.ty += WHIP_SIGN * cfg.whipYaw * early * whipIn;
    // Past AIR_PITCH_HELP of nose up or down the rider's body works the bike back toward a landable angle (owner
    // point 12, a recovery help); inside it the throttle and the rear brake alone set the pitch, as before.
    const overPitch = Math.max(0, Math.abs(pitchNow0) - AIR_PITCH_HELP);
    if (overPitch > 0) { const tp = Math.sign(pitchNow0) * AIR_PITCH_KP * overPitch; A.tx += lx * tp; A.ty += ly * tp; A.tz += lz * tp; }
    // ...and drifts back toward where it flies (bars and hips against the wheels' gyro), weakly while the whip is held.
    // Simplified straightens it firmly once the input is let go, so a beginner's whip lands straight.
    const vh = Math.sqrt(V.x * V.x + V.z * V.z);
    if (vh > 1) {
      const fh = Math.sqrt(fx * fx + fz * fz) || 1;
      const yawError = Math.atan2((fz * V.x - fx * V.z) / fh, (fx * V.x + fz * V.z) / fh);
      const kp = (input.assist ? ASSIST.airYawKp : cfg.airYawKp) * (1 - 0.8 * Math.abs(whipIn));
      A.ty += kp * yawError - cfg.airYawKd * W.y;
    }
  } else {
    const rec = s.recoverFor > 0 ? s.recoverFor / cfg.recoverTime : 0; // recovery under way (noCrash), 1 -> 0
    // Arms and legs as extra suspension: the rider's limbs resist the bike pitching under him, more when standing.
    const pitchRate = W.x * lx + W.y * ly + W.z * lz;
    // Standing, knees and elbows let the bike pitch under the body and damp it (row 9); seated the hips ride it.
    const damp = cfg.limbPitchDamping * (cfg.seatedLimbDamping + (1 - cfg.seatedLimbDamping) * s.rider.stand);
    A.tx -= lx * damp * pitchRate; A.ty -= ly * damp * pitchRate; A.tz -= lz * damp * pitchRate;
    // Catching a slide: with the rear stepping out the rider steers into it (front tyre on the ground), pulling the
    // heading back toward the direction of travel. It cannot beat a rear held locked for long: that spins.
    const vhs = Math.sqrt(V.x * V.x + V.z * V.z);
    if (frontW.contact && vhs > 2) {
      const fh = Math.sqrt(fx * fx + fz * fz) || 1;
      const beta = Math.atan2((fz * V.x - fx * V.z) / fh, (fx * V.x + fz * V.z) / fh);
      // (Recovering, the rider steers into the slide with all he has: the bike comes back in line with its travel
      // over the recovery, not at once: owner point 12.)
      const g = 1 + 2 * rec;
      if (Math.abs(beta) > 0.1 && Math.abs(beta) < 1.4) A.ty += clamp(cfg.slideCatch * g * (beta - 0.1 * Math.sign(beta)), -cfg.slideCatchMax * g, cfg.slideCatchMax * g);
    }
    // Rider balance (steering under the bike) through the front tyre: the less grip the front has left, the less the
    // rider can hold the lean; a sliding front cannot hold it at all. The foot out adds a leg on the ground in slow turns.
    const frontAuthority = frontW.contact ? clamp(1.6 - 0.6 * frontLateralUsage, 0.05, 1) : 0.5;
    // Steering the bike back under itself takes speed: the rider's balance torque is capped, low at walking pace,
    // and further by what the front tyre has left.
    const cap = input.assist
      ? cfg.balanceTorque * ASSIST.balanceGain * Math.max(0.6, frontAuthority)
      : cfg.balanceTorque * clamp(Math.abs(speed) / cfg.balanceSpeed, 0.7, 1) * frontAuthority;
    const t = clamp(cfg.leanKp * (targetRoll - roll) - cfg.leanKd * rollRate, -cap, cap);
    // The foot is a leg on the ground: its own capped push, only in slow turns.
    // A slide at low speed gets a dab: the inside foot goes down by instinct, as it would for any rider.
    // ...and so does a bike going over well past the lean the rider wanted (a slow turn up a face, a rut that tips it).
    const overLean = (Math.abs(roll) - maxLean - DAB_OVER_LEAN) / 0.1;
    // ...and coming to a stop both feet go down and hold the bike upright (it toppled over at a standstill with the
    // bars turned, owner 2026-10-06: a hard stop is not a crash).
    const stopped = clamp(1 - Math.abs(speed) / STOP_FEET, 0, 1);
    const dab = clamp(Math.max((s.telemetry.sideslip - 0.3) / 0.3, overLean, stopped), 0, 1);
    // (Both feet at a stop hold far more than one dabbing foot: STOP_TORQUE.)
    const footCap = Math.max(input.assist ? ASSIST.uprightTorque * Math.max(lowSpeed, 0.3) : cfg.footTorque * Math.max(s.rider.foot, dab) * lowSpeed, STOP_TORQUE * stopped);
    const tf = clamp(cfg.footRollKp * (targetRoll * (1 - stopped) - roll) - (cfg.footRollKp / 4) * rollRate, -footCap, footCap);
    // Simplified mode: past the lean limit a guard pushes the bike back up (the assist never lets it lie down in a turn).
    // Real mode at speed too: past the rider's lean limit (+ LEAN_GUARD_MARGIN) he gets the bike back up with his body
    // and bars, even when a slide has just let go and snapped it over (slow turns are the foot's business, coach 2).
    const over = input.assist ? Math.max(0, Math.abs(roll) - ASSIST.maxLean - 0.08)
      : Math.abs(speed) > cfg.footMaxSpeed ? Math.max(0, Math.abs(roll) - cfg.maxLean - cfg.footLeanBonus * footDown - LEAN_GUARD_MARGIN) : 0;
    const tg = over <= 0 ? 0 : input.assist ? -Math.sign(roll) * ASSIST.leanGuardKp * over - ASSIST.leanGuardKd * rollRate
      : -Math.sign(roll) * LEAN_GUARD_KP * over - LEAN_GUARD_KD * rollRate;
    // Recovery (noCrash): a limit was crossed; the rider stands the bike up with everything he has for recoverTime,
    // fading out so nothing snaps. The alignment with the travel is the slide catch above, boosted the same way.
    const tr = rec > 0 ? clamp(-cfg.recoverTorque * rec * Math.sign(roll) * Math.min(1, Math.abs(roll) / 0.3) - 400 * rec * rollRate, -cfg.recoverTorque, cfg.recoverTorque) : 0;
    // A rider who is down no longer balances anything: the bike goes where its tyres and gravity take it.
    const held = (t + tf + tg + tr) * (s.telemetry.fallen ? 0 : 1);
    A.tx -= fx * held; A.ty -= fy * held; A.tz -= fz * held;
  }

  // The rider as a sprung mass (two bodies, exact in translation): his deviation d from his posture's place obeys
  // d'' (1 - mu) = F / mRider - F_ng / M, where F is the seat or the legs on him (holding his weight, plus the spring
  // and damper on d) and F_ng the outside forces on the whole bike except gravity. The chassis then accelerates
  // with A / M - mu d'', so what the legs soak never reaches the frame.
  const mr = cfg.riderMass, st = rd.stand;
  const kr = cfg.seatStiffness + (cfg.pegStiffness - cfg.seatStiffness) * st, cr = cfg.seatDamping + (cfg.pegDamping - cfg.seatDamping) * st;
  const ngx = A.fx, ngy = A.fy + cfg.mass * GRAVITY, ngz = A.fz;
  let Fy = mr * GRAVITY * uy - kr * rd.dy - cr * rd.vy, Fz = mr * GRAVITY * fy - kr * rd.dz - cr * rd.vz;
  // Out of leg travel: knees fully bent (or straight) is a hard stop, and how fast he hit it is what he could not soak.
  let legsHit = 0;
  const over = Math.abs(rd.dy) - cfg.legTravel;
  if (over > 0) {
    Fy -= Math.sign(rd.dy) * cfg.legStopStiffness * over;
    if (rd.vy * rd.dy > 0) legsHit = Math.abs(rd.vy);
  }
  const ay = (Fy / mr - (ngx * ux + ngy * uy + ngz * uz) / cfg.mass) / (1 - mu);
  const az = (Fz / mr - (ngx * fx + ngy * fy + ngz * fz) / cfg.mass) / (1 - mu);
  rd.vy += ay * dt; rd.vz += az * dt;
  rd.dy += rd.vy * dt; rd.dz += rd.vz * dt;
  if (Math.abs(rd.dz) > cfg.legTravel) { rd.dz = Math.sign(rd.dz) * cfg.legTravel; rd.vz = 0; }
  // Integrate (semi-implicit Euler), in place.
  const vbx = V.x, vby = V.y, vbz = V.z;
  const lin = 1 - (airborne ? 0 : cfg.linearDrag) * dt, im = dt / cfg.mass;
  const rax = mu * (ux * ay + fx * az), ray = mu * (uy * ay + fy * az), raz = mu * (uz * ay + fz * az);
  V.x = (V.x + A.fx * im - rax * dt) * lin; V.y = (V.y + A.fy * im - ray * dt) * lin; V.z = (V.z + A.fz * im - raz * dt) * lin;
  const I = cfg.inertia;
  // Body frame = R^T world.
  const wbx = ROT[0]! * W.x + ROT[3]! * W.y + ROT[6]! * W.z, wby = ROT[1]! * W.x + ROT[4]! * W.y + ROT[7]! * W.z, wbz = ROT[2]! * W.x + ROT[5]! * W.y + ROT[8]! * W.z;
  const tbx = ROT[0]! * A.tx + ROT[3]! * A.ty + ROT[6]! * A.tz, tby = ROT[1]! * A.tx + ROT[4]! * A.ty + ROT[7]! * A.tz, tbz = ROT[2]! * A.tx + ROT[5]! * A.ty + ROT[8]! * A.tz;
  const Lx = I.x * wbx, Ly = I.y * wby, Lz = I.z * wbz;
  const abx = (tbx - (wby * Lz - wbz * Ly)) / I.x, aby = (tby - (wbz * Lx - wbx * Lz)) / I.y, abz = (tbz - (wbx * Ly - wby * Lx)) / I.z;
  // Angular momentum is kept in the air; on the ground a little damping stands in for everything unmodelled.
  const ang = 1 - (airborne ? 0 : cfg.angularDrag) * dt;
  W.x = (W.x + (ROT[0]! * abx + ROT[1]! * aby + ROT[2]! * abz) * dt) * ang;
  W.y = (W.y + (ROT[3]! * abx + ROT[4]! * aby + ROT[5]! * abz) * dt) * ang;
  W.z = (W.z + (ROT[6]! * abx + ROT[7]! * aby + ROT[8]! * abz) * dt) * ang;
  P.x += V.x * dt; P.y += V.y * dt; P.z += V.z * dt;
  if (HIT.nx !== 0 || HIT.nz !== 0) {
    // Out of a hard obstacle along its normal, and no velocity left into it.
    P.x += HIT.px; P.z += HIT.pz;
    const vn = V.x * HIT.nx + V.z * HIT.nz;
    if (vn < 0) { V.x -= vn * HIT.nx; V.z -= vn * HIT.nz; }
    HIT.nx = HIT.nz = 0;
  }
  // Orientation: q += 0.5 dt (w, 0) * q, normalised.
  {
    const qx = Q.x, qy = Q.y, qz = Q.z, qw = Q.w, h = 0.5 * dt;
    const dxq = W.x * qw + W.y * qz - W.z * qy, dyq = -W.x * qz + W.y * qw + W.z * qx;
    const dzq = W.x * qy - W.y * qx + W.z * qw, dwq = -W.x * qx - W.y * qy - W.z * qz;
    const nx = qx + h * dxq, ny = qy + h * dyq, nz = qz + h * dzq, nw = qw + h * dwq;
    const l = Math.sqrt(nx * nx + ny * ny + nz * nz + nw * nw);
    Q.x = nx / l; Q.y = ny / l; Q.z = nz / l; Q.w = nw / l;
  }
  // Body moves in the air swap angle between rider and bike (no torque, no lasting spin): back lifts the nose.
  if (airborne) {
    const body = s.rider.body - bodyPrev;
    if (body !== 0) {
      // q = rotation(left, a) * q
      const half = (cfg.airBodyPitch * body) / 2, sn = Math.sin(half);
      const ax = lx * sn, ay = ly * sn, az = lz * sn, aw = Math.cos(half);
      const qx = Q.x, qy = Q.y, qz = Q.z, qw = Q.w;
      Q.w = aw * qw - ax * qx - ay * qy - az * qz;
      Q.x = aw * qx + ax * qw + ay * qz - az * qy;
      Q.y = aw * qy - ax * qz + ay * qw + az * qx;
      Q.z = aw * qz + ax * qy - ay * qx + az * qw;
    }
  }

  const check = P.x + P.y + P.z + V.x + V.y + V.z + W.x + W.y + W.z + Q.x + Q.y + Q.z + Q.w + rearW.omega + frontW.omega;
  if (!Number.isFinite(check) || Math.sqrt(V.x * V.x + V.y * V.y + V.z * V.z) > MAX_SPEED || Math.sqrt(W.x * W.x + W.y * W.y + W.z * W.z) > MAX_SPIN) throw new Error('bike state diverged');

  // Outcomes.
  const nfx = 2 * (Q.x * Q.z + Q.y * Q.w), nfy = 2 * (Q.y * Q.z - Q.x * Q.w), nfz = 1 - 2 * (Q.x * Q.x + Q.y * Q.y);
  const nuy = 1 - 2 * (Q.x * Q.x + Q.z * Q.z);
  const newSpeed = V.x * nfx + V.y * nfy + V.z * nfz;
  const tel = s.telemetry;
  const wasFallen = tel.fallen;
  tel.landing = null;
  if (grounded && s.airTime >= LANDING_MIN_AIR && !wasFallen) {
    let nx = (rearC.active ? rearC.nx : 0) + (frontC.active ? frontC.nx : 0);
    let ny = (rearC.active ? rearC.ny : 0) + (frontC.active ? frontC.ny : 0);
    let nz = (rearC.active ? rearC.nz : 0) + (frontC.active ? frontC.nz : 0);
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1; nx /= nl; ny /= nl; nz /= nl;
    // The grade: nose vs the landing slope and the speed into it. It decides nothing: whether the rider stays on is
    // up to the suspension and his legs over the next LANDING_WINDOW (checkLimits).
    // (A landing is a rare event: its result object is the one allocation a step can make.)
    tel.landing = judgeLanding(Math.asin(clamp(fx * nx + fy * ny + fz * nz, -1, 1)), -(vbx * nx + vby * ny + vbz * nz), s.airTime, cfg);
    s.crashIn = LANDING_WINDOW;
  }
  if (s.crashIn > 0) s.crashIn = Math.max(0, s.crashIn - dt);
  s.airTime = grounded ? 0 : s.airTime + dt;

  const pitch = Math.asin(clamp(nfy, -1, 1));
  // Front sliding past its grip while it carries a lean or a brake: bolt upright with a locked front the rider can
  // still let go in time, leaned over the side force is gone at once.
  const leaned = Math.abs(roll) > 0.12;
  const brakingOrLeaned = leverFront > 0.05 * cfg.frontBrakeTorque && (leaned || frontW.slipRatio < -0.5);
  s.washout = frontSliding && brakingOrLeaned ? s.washout + dt : 0;
  const vh = Math.sqrt(V.x * V.x + V.z * V.z);
  const sideslip = grounded && vh > 1 ? Math.acos(clamp((V.x * nfx + V.z * nfz) / (vh * Math.sqrt(nfx * nfx + nfz * nfz) || 1), -1, 1)) : 0;
  s.spinFor = grounded && vh > 3 && sideslip > SPIN_OUT && sideslip < Math.PI - 0.3 ? s.spinFor + dt : 0;

  const m = LIMITS;
  m.upDot = nuy; m.pitch = pitch;
  m.frontOnly = frontW.contact && !rearW.contact; m.rearOnly = rearW.contact && !frontW.contact;
  m.liftVsGround = m.rearOnly ? Math.asin(clamp(nfx * rearC.nx + nfy * rearC.ny + nfz * rearC.nz, -1, 1)) : 0;
  m.frontSlideTime = s.washout; m.leaned = leaned; m.spinTime = s.spinFor;
  m.hitSpeed = hitNow ? tel.hitInfo.speed : 0;
  m.bottomRate = A.bottom; m.legsBottomRate = legsHit;
  const limit = wasFallen ? tel.fallCause : checkLimits(m, cfg, SPIN_TIME);
  // noCrash (owner 2026-10-06): a limit crossed is not a fall. It starts a recovery: the balance torque above stands
  // the bike up, the throttle eases (below), the view shakes and the coach names what went wrong. A tree hit is the
  // one limit that still stops the bike dead; it keeps its cause as the recovery's.
  const recovering = cfg.noCrash && limit !== null && !wasFallen;
  if (recovering) { s.recoverFor = cfg.recoverTime; tel.recovery = limit; s.washout = 0; s.spinFor = 0; }
  else if (s.recoverFor > 0) { s.recoverFor = Math.max(0, s.recoverFor - dt); if (s.recoverFor === 0) tel.recovery = null; }
  const cause = recovering ? null : limit;
  if (cause === 'landing' && tel.lastLanding && !tel.landing) tel.lastLanding.verdict = 'crash';
  if (cause === 'landing' && tel.landing) tel.landing.verdict = 'crash';

  tel.speed = newSpeed;
  tel.rearSlip = rearW.contact ? Math.max(0, rearW.slipRatio) / cfg.slipPeak : 0;
  tel.frontLock = frontW.contact && Math.abs(newSpeed) > 1 && leverFront > 0 && frontW.slipRatio < -0.5;
  tel.airborne = airborne;
  tel.fallen = wasFallen || cause !== null;
  tel.rpm = s.engine.rpm;
  tel.gear = s.engine.gear;
  tel.frontBrake = leverFront;
  tel.rearBrake = leverRear;
  tel.rearLock = rearW.contact && Math.abs(newSpeed) > 1 && rearW.slipRatio < -0.5;
  tel.frontWashout = s.washout > 0;
  tel.wheelie = rearW.contact && !frontW.contact;
  tel.pitch = pitch;
  tel.roll = roll;
  tel.sideslip = sideslip;
  if (tel.landing) tel.lastLanding = tel.landing;
  tel.fallCause = cause;
  tel.steerAngle = steerAngle;
  tel.hit = hitNow ? tel.hitInfo : null;
  tel.conesHit = s.conesHit;
  tel.reversing = s.paddling;
  tel.bottomRate = A.bottom;
}
